import { useSearchParams } from 'react-router-dom';
import { useFetchData } from '../../../../hooks';
import { PageableResponse } from '../../../../types';
import { BillOfMaterialDetailList } from '../../../../types/BillOfMaterial';
import { BOM_API } from '../../../../hooks/useBillOfMaterialHooks';
import { RequestStatusEnum, Resource } from '../../../../lib/enums';
import {
  convertDate,
  dateFormat,
  dateTimeFormat,
  getEnumValue,
  roundAndFormat,
} from '../../../../lib/utils';
import { useBillOfMaterialDetailStore } from '../../../../store/BillOfMaterialStore';
import React, { useEffect, useState } from 'react';
import { Table, TableColumnsType } from 'antd';
import StatusTag from '../../../../components/ui/StatusTag';
import ErrorModal from '../../../../components/ui/modal/ErrorModal';
import dayjs from 'dayjs';

const BillOfMaterialDetailTable = () => {
  const [searchParams] = useSearchParams();

  const {
    data: bomList,
    isLoading,
    error,
    isError,
  } = useFetchData<PageableResponse<BillOfMaterialDetailList>>(
    `${BOM_API}/details`,
    {
      resource: searchParams.get('resource') || Resource.FULL,
      search: searchParams.get('search') || '',
      from:
        searchParams.get('from') === undefined ||
        searchParams.get('from') === null
          ? dayjs().subtract(6, 'month').format(dateFormat)
          : searchParams.get('from'),
      to:
        searchParams.get('to') === undefined || searchParams.get('to') === null
          ? dayjs().add(2, 'month').format(dateFormat)
          : searchParams.get('to'),
      status: getEnumValue(RequestStatusEnum, searchParams.get('status') ?? ''),
      length: parseInt(searchParams.get('length') || '999999'),
    },
  );

  const { setData, resetSelectedRowKeys } = useBillOfMaterialDetailStore();

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setData(newSelectedRowKeys);
  };

  useEffect(() => {
    resetSelectedRowKeys();
  }, [resetSelectedRowKeys]);

  const rowSelection = {
    selectedRowKeys: useBillOfMaterialDetailStore((state) => state.data),
    onChange: onSelectChange,
  };

  useEffect(() => {
    setIsErrorModalOpen(isError);
  }, [error?.message, isError]);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const columns: TableColumnsType<BillOfMaterialDetailList> = [
    {
      title: 'Proder No',
      dataIndex: 'proderNo',
      width: 120,
      render: (_text, record) => <span>{record.proderNo || record.id}</span>,
    },
    {
      title: 'Packing No',
      dataIndex: 'setPackingNo',
    },
    {
      title: 'MR No',
      dataIndex: 'mrNo',
    },
    {
      title: 'Process Group',
      dataIndex: 'processGroup',
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
    },
    {
      title: 'Product',
      dataIndex: 'product',
      render: (_text, record) => <span>{record.product.description}</span>,
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
    },
    {
      title: 'Size',
      dataIndex: 'size',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      render: (_text, record) => <span>{roundAndFormat(record.quantity)}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'buyercode',
      width: 80,
      render: (_text, record) => (
        <StatusTag status={record.status.toUpperCase().replace(' ', '_')} />
      ),
    },
    {
      title: 'Prepared By',
      dataIndex: 'preparedBy',
    },
    {
      title: 'Created By/Date',
      dataIndex: 'createdBy',
      render: (createdBy, record) => (
        <div className="flex flex-col">
          <span className="mr-2">{createdBy}</span>
          {record.timeCreated != null && (
            <span>{convertDate(record.timeCreated, dateTimeFormat)}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Updated By/Date',
      dataIndex: 'updatedBy',
      render: (updatedBy, record) => (
        <div className="flex flex-col">
          <span className="mr-2">{updatedBy}</span>
          {record.timeUpdated != null && (
            <span>{convertDate(record.timeUpdated, dateTimeFormat)}</span>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Table
        rowKey={(record) => record.id}
        rowSelection={rowSelection}
        columns={columns}
        loading={isLoading}
        dataSource={bomList?.data}
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
      />

      <ErrorModal
        visible={isErrorModalOpen}
        message={error?.message}
        onClose={handleErrorModalClose}
      />
    </>
  );
};

export default BillOfMaterialDetailTable;
